import { cn } from '@v2/shared/lib/utils';
import { Tab, TabDescriptor } from './types';

type Props = {
  tabs: TabDescriptor[];
  current: Tab;
  onSelect: (tab: Tab) => void;
  buildUrl: (path: string) => string;
};

const TAB_CLASS = cn(
  'flex cursor-pointer items-center gap-2 rounded-lg border-0 px-4 py-2',
  'text-sm font-semibold no-underline'
);

/**
 * The two states are complete class strings rather than a base with an override, because
 * `cn` is clsx: it does not resolve conflicting Tailwind utilities, so `bg-white` layered
 * over `bg-transparent` is decided by whichever the stylesheet defines last.
 */
const CURRENT_CLASS = 'bg-white text-slate-900 shadow-sm';

const RESTING_CLASS = 'bg-transparent text-slate-600 hover:text-slate-900';

/**
 * Seller reviews and product reviews.
 *
 * These are the one piece of page state that reaches the url — a link to the magazine
 * reviews is worth being able to send someone, where a link to page 7 of a four-star
 * filter is not. So they are real anchors with real hrefs, which a crawler follows and a
 * visitor can middle-click, and the click handler turns an ordinary activation into an
 * API call plus a pushState instead of a page load.
 *
 * Modified clicks are left alone deliberately: ctrl-click and cmd-click mean "open this
 * somewhere else", and swallowing them would break the very thing the href is here for.
 *
 * No loading state of its own: a spinner appearing beside the count widened the tab and
 * pushed the row about while the reviews were being fetched. ReviewList shows the one
 * indicator, over the content that is actually changing.
 */
export default function Tabs({ tabs, current, onSelect, buildUrl }: Props) {
  return (
    <nav aria-label="Review type">
      <ul className="m-0 flex list-none gap-1 rounded-xl bg-slate-100 p-1">
        {tabs.map((tab) => {
          const isCurrent = tab.key === current;

          return (
            <li key={tab.key}>
              <a
                href={buildUrl(tab.url)}
                aria-current={isCurrent ? 'page' : undefined}
                onClick={(event) => {
                  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
                    return;
                  }

                  event.preventDefault();
                  onSelect(tab.key);
                }}
                className={cn(TAB_CLASS, isCurrent ? CURRENT_CLASS : RESTING_CLASS)}
              >
                {tab.label} <span className="font-normal">· {tab.count.toLocaleString('en-US')}</span>
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
